import React, { useEffect, useMemo, useState } from 'react';
import {
  Typography,
  Button,
  Grid,
  TextField,
  Box,
  CircularProgress,
  Paper,
  Divider,
  Alert,
  Checkbox,
  FormControlLabel
} from '@mui/material';

import CustomerLayout from '../CustomerLayout';
import { useLocation } from 'react-router-dom';
import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { API } from '../../../config';

const stripePublishableKey = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

const stripePromise = stripePublishableKey
  ? loadStripe(stripePublishableKey)
  : null;

const CheckoutForm = ({ onPaymentSuccess }) => {
  const stripe = useStripe();
  const elements = useElements();

  const [submitting, setSubmitting] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [paymentReady, setPaymentReady] = useState(false);
  const [message, setMessage] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements || submitting || paymentCompleted) return;

    setSubmitting(true);
    setMessage('');

    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      redirect: 'if_required',
    });

    if (error) {
      setMessage(error.message || 'Payment failed.');
      setSubmitting(false);
      return;
    }

    if (paymentIntent) {
      setMessage(`Payment status: ${paymentIntent.status}`);

      if (
         paymentIntent.status === 'succeeded' ||
         paymentIntent.status === 'processing'
      ) {
         setPaymentCompleted(true);
         onPaymentSuccess?.(paymentIntent);
      }
    } else {
      setMessage('Payment submitted. Waiting for confirmation...');
      setPaymentCompleted(true);
    }

    setSubmitting(false);
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement
        onReady={() => {
          console.log('Stripe payment form ready');
          setPaymentReady(true);
        }}
        onLoadError={(event) => {
          console.error('Stripe PaymentElement error:', event);
          setPaymentReady(false);
          setMessage(event?.error?.message || 'Payment form failed to load.');
        }}
      />

      {message && (
        <Alert
          severity={message.toLowerCase().includes('failed') ? 'error' : 'success'}
          sx={{ mt: 3 }}
        >
          {message}
        </Alert>
      )}

      <Button
        type="submit"
        variant="contained"
        color="primary"
        size="large"
        fullWidth
        disabled={!stripe || submitting || !paymentReady || paymentCompleted}
        sx={{
          mt: 3,
          py: 1.5,
          borderRadius: 2,
          fontWeight: 600,
          textTransform: 'none',
          fontSize: '1rem',
        }}
      >
        {paymentCompleted
          ? 'Payment Submitted'
          : submitting
          ? 'Processing Payment...'
          : 'Pay Now'}
      </Button>
    </form>
  );
};

const CustomerPaymentManagement = () => {
  const [paymentInfo, setPaymentInfo] = useState({
    cardNumber: '',
    cardholderName: '',
    expirationDate: '',
    cvv: '',
  });

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const orderIdFromUrl = queryParams.get('orderId');

  const [orderId, setOrderId] = useState(orderIdFromUrl || '');

  const [clientSecret, setClientSecret] = useState('');
  const [loadingIntent, setLoadingIntent] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');

  const [paymentQuote, setPaymentQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState('');
  const [acceptedTotal, setAcceptedTotal] = useState(false);


useEffect(() => {
  const id = Number(orderId);

  setPaymentQuote(null);
  setAcceptedTotal(false);
  setQuoteError('');

  if (!Number.isSafeInteger(id) || id <= 0 || clientSecret) {
    setQuoteLoading(false);
    return;
  }

  const controller = new AbortController();

  const loadQuote = async () => {
    setQuoteLoading(true);

    try {
      const token =
        localStorage.getItem('customer_token') ||
        localStorage.getItem('token');

      if (!token) {
        throw new Error('Please sign in to view payment details.');
      }

      const response = await fetch(
        `${API}/api/payments/quote/${id}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
          signal: controller.signal,
        }
      );

      const data = await response.json();

      if (!response.ok) {
        throw new Error(
          data.message || 'Unable to retrieve payment quote.'
        );
      }

      if (
        data.orderId !== id ||
        !Number.isSafeInteger(data.carrierRateCents) ||
        !Number.isSafeInteger(data.processingFeeCents) ||
        !Number.isSafeInteger(data.totalCents) ||
        data.carrierRateCents <= 0 ||
        data.processingFeeCents < 0 ||
        data.totalCents !==
          data.carrierRateCents + data.processingFeeCents
      ) {
        throw new Error('Invalid payment quote received.');
      }

      if (!controller.signal.aborted) {
        setPaymentQuote(data);
      }
    } catch (err) {
      if (!controller.signal.aborted) {
        setQuoteError(err.message);
      }
    } finally {
      if (!controller.signal.aborted) {
        setQuoteLoading(false);
      }
    }
  };

  loadQuote();

  return () => controller.abort();
}, [orderId, clientSecret]);



  const elementsOptions = useMemo(() => {
    if (!clientSecret) return null;

    return {
      clientSecret,
      appearance: {
        theme: 'stripe',
      },
    };
  }, [clientSecret]);

  const handleCreatePaymentIntent = async (e) => {
    e.preventDefault();

      if (
        !paymentQuote ||
        !acceptedTotal ||
        Number(paymentQuote.orderId) !== Number(orderId)
      ) {
        setErrorMessage('Please review and accept the payment total.');
        return;
      }

    setErrorMessage('');
    setSuccessMessage('');
    setLoadingIntent(true);

    try {
      const token =
        localStorage.getItem('customer_token') ||
        localStorage.getItem('token');

      if (!token) {
        setErrorMessage('Customer token not found. Please sign in again.');
        setLoadingIntent(false);
        return;
      }

      if (!orderId) {
        setErrorMessage('Order ID is required to start payment.');
        setLoadingIntent(false);
        return;
      }

      const response = await fetch(`${API}/api/payments/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
       body: JSON.stringify({
           orderId: Number(orderId),
           acceptedTotalCents: paymentQuote.totalCents,
           acceptedFeePolicy: paymentQuote.feePolicy,
           acceptedPaymentTerms: acceptedTotal,
       }),
      });

      const data = await response.json();

      if (!response.ok) {
        setErrorMessage(data.message || 'Could not start payment.');
        setLoadingIntent(false);
        return;
      }

      if (
        !Number.isFinite(Number(data.amount)) ||
        Math.round(Number(data.amount) * 100) !== paymentQuote.totalCents
      ) {
        setErrorMessage(
          'Payment amount does not match the approved quote. Please do not continue.'
        );
        setLoadingIntent(false);
        return;
      }

      setClientSecret(data.clientSecret);

      setSuccessMessage(
        `Payment started for load/order #${orderId}. Amount: $${data.amount}`
      );
    } catch (err) {
      console.error('Payment intent creation error:', err);
      setErrorMessage('Network or server error while starting payment.');
    }

    setLoadingIntent(false);
  };

  const handlePaymentSuccess = (paymentIntent) => {
    setSuccessMessage(
      `Payment submitted successfully. Stripe status: ${paymentIntent.status}`
    );
  };

  return (
    <CustomerLayout>
      <Box sx={{ p: { xs: 2, md: 3 } }}>
        <Box sx={{ mb: 3, textAlign: 'left' }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Payments
          </Typography>

          <Typography variant="body1" color="text.secondary">
            Securely complete load payments, review payment status, and manage active transportation transactions.
          </Typography>
        </Box>

        <Paper
          elevation={2}
          sx={{
            p: { xs: 2, md: 4 },
            borderRadius: 4,
          }}
        >
          {!clientSecret && (
            <form onSubmit={handleCreatePaymentIntent}>
              <Typography variant="h5" fontWeight={700} gutterBottom>
                Start Payment
              </Typography>

              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ mb: 3 }}
              >
                Enter or confirm the load/order ID, then continue to secure payment processing.
              </Typography>

              <Divider sx={{ mb: 3 }} />

              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Order ID"
                    variant="outlined"
                    fullWidth
                    value={orderId}
                    onChange={(e) => setOrderId(e.target.value)}
                    disabled={Boolean(orderIdFromUrl)}
                    required
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Cardholder Name (Optional)"
                    variant="outlined"
                    fullWidth
                    value={paymentInfo.cardholderName}
                    onChange={(e) =>
                      setPaymentInfo({
                        ...paymentInfo,
                        cardholderName: e.target.value,
                      })
                    }
                  />
                </Grid>


{quoteLoading && (
  <Grid item xs={12}>
    <CircularProgress size={24} />
  </Grid>
)}

{quoteError && (
  <Grid item xs={12}>
    <Alert severity="error">{quoteError}</Alert>
  </Grid>
)}

{paymentQuote && (
  <Grid item xs={12}>
    <Paper variant="outlined" sx={{ p: 3, borderRadius: 2 }}>
      <Typography variant="h6" fontWeight={700} gutterBottom>
        Payment Summary
      </Typography>

      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography>Agreed carrier rate</Typography>
        <Typography>
          ${(paymentQuote.carrierRateCents / 100).toFixed(2)}
        </Typography>
      </Box>

      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography>Payment processing fee</Typography>
        <Typography>
          ${(paymentQuote.processingFeeCents / 100).toFixed(2)}
        </Typography>
      </Box>

      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 2 }}
      >
        No additional processing fee is currently enabled.
      </Typography>

      <Divider sx={{ mb: 2 }} />

      <Box display="flex" justifyContent="space-between" mb={2}>
        <Typography fontWeight={700}>
          Total Customer Payment
        </Typography>
        <Typography fontWeight={700}>
          ${(paymentQuote.totalCents / 100).toFixed(2)}
        </Typography>
      </Box>

      <FormControlLabel
        control={
          <Checkbox
            checked={acceptedTotal}
            onChange={(e) => setAcceptedTotal(e.target.checked)}
          />
        }
        label="I have reviewed and agree to the total payment amount and applicable cancellation and refund terms."
      />
    </Paper>
  </Grid>
)}

                <Grid item xs={12}>
                  <Button
                    type="submit"
                    variant="contained"
                    color="primary"
                    size="large"
                   disabled={
                     loadingIntent ||
                     quoteLoading ||
                     !paymentQuote ||
                     Number(paymentQuote.orderId) !== Number(orderId) ||
                     !acceptedTotal ||
                     !orderId
                    }
                    sx={{
                      py: 1.5,
                      borderRadius: 2,
                      fontWeight: 600,
                      textTransform: 'none',
                    }}
                  >
                    {loadingIntent ? (
                      <>
                        <CircularProgress
                          size={20}
                          sx={{ mr: 1, color: 'white' }}
                        />
                        Starting Payment...
                      </>
                    ) : (
                      'Start Secure Payment'
                    )}
                  </Button>
                </Grid>
              </Grid>
            </form>
          )}

          {errorMessage && (
            <Alert severity="error" sx={{ mt: 3 }}>
              {errorMessage}
            </Alert>
          )}

          {successMessage && (
            <Alert severity="success" sx={{ mt: 3 }}>
              {successMessage}
            </Alert>
          )}

          {!stripePublishableKey && (
            <Alert severity="warning" sx={{ mt: 3 }}>
              Stripe publishable key is missing.
            </Alert>
          )}

          {clientSecret && elementsOptions && stripePromise && (
            <Paper
              elevation={3}
              sx={{
                maxWidth: 700,
                mx: 'auto',
                mt: 4,
                p: { xs: 2, md: 4 },
                borderRadius: 4,
              }}
            >
              <Box sx={{ mb: 3 }}>
                <Typography variant="body1">
                  <strong>Order ID:</strong> #{orderId}
                </Typography>

                <Typography
                  variant="body2"
                  color="text.secondary"
                >
                  Payments are securely processed through Stripe.
                </Typography>
              </Box>

              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ mb: 3, textAlign: 'center' }}
              >
                Secure Payment
              </Typography>

              <Elements stripe={stripePromise} options={elementsOptions}>
                <CheckoutForm onPaymentSuccess={handlePaymentSuccess} />
              </Elements>
            </Paper>
          )}
        </Paper>
      </Box>
    </CustomerLayout>
  );
};

export default CustomerPaymentManagement;